/**
 * Certificate Management Service
 * 
 * Handles all certificate-related API calls including listing,
 * creating, updating, and deleting certificates
 */

import { api, handleApiError } from './api';
import type {
  CertificateListItem,
  CertificateFilters,
  CreateCertificatePayload,
  UpdateCertificatePayload,
  PaginationParams,
  PaginatedResponse,
} from '@/types';

/**
 * Mock certificate data for development
 */
const MOCK_CERTIFICATES: CertificateListItem[] = [
  {
    id: '1',
    ref: '550e8400-e29b-41d4-a716-446655440001',
    organizationId: 1,
    alias: 'Production SSL Certificate',
    expiresAt: '2025-12-31T23:59:59Z',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    status: 'valid',
  },
  {
    id: '2',
    ref: '550e8400-e29b-41d4-a716-446655440002',
    organizationId: 1,
    alias: 'Staging Certificate',
    expiresAt: '2025-12-15T23:59:59Z',
    createdAt: '2023-12-10T14:20:00Z',
    updatedAt: '2023-12-10T14:20:00Z',
    status: 'valid',
  },
  {
    id: '3',
    ref: '550e8400-e29b-41d4-a716-446655440003',
    organizationId: 1,
    alias: 'Development Certificate',
    expiresAt: '2025-11-20T23:59:59Z',
    createdAt: '2023-11-01T09:15:00Z',
    updatedAt: '2023-11-01T09:15:00Z',
    status: 'expiring',
  },
  {
    id: '4',
    ref: '550e8400-e29b-41d4-a716-446655440004',
    organizationId: 1,
    alias: 'Old Certificate',
    expiresAt: '2024-10-31T23:59:59Z',
    createdAt: '2023-06-05T16:45:00Z',
    updatedAt: '2023-06-05T16:45:00Z',
    status: 'expired',
  },
  {
    id: '5',
    ref: '550e8400-e29b-41d4-a716-446655440005',
    organizationId: 1,
    alias: 'API Gateway Certificate',
    expiresAt: '2026-03-15T23:59:59Z',
    createdAt: '2024-02-20T11:00:00Z',
    updatedAt: '2024-02-20T11:00:00Z',
    status: 'valid',
  },
];

/**
 * Calculate certificate status based on expiration date
 */
const calculateStatus = (expiresAt: string): 'valid' | 'expiring' | 'expired' => {
  const expirationDate = new Date(expiresAt);
  const today = new Date();
  const daysUntilExpiration = Math.floor(
    (expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysUntilExpiration < 0) {
    return 'expired';
  } else if (daysUntilExpiration <= 30) {
    return 'expiring';
  }
  return 'valid';
};

/**
 * Certificate Service Object
 * Contains all certificate management API operations
 */
export const certificateService = {
  /**
   * Get Certificates List
   * Fetches paginated list of certificates with optional filters
   * 
   * @param params - Pagination parameters
   * @param filters - Optional filters for certificates
   * @returns Promise with paginated certificate list
   */
  getCertificates: async (
    params: PaginationParams,
    filters?: CertificateFilters
  ): Promise<PaginatedResponse<CertificateListItem>> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
        ...(params.search && { search: params.search }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.organizationId && { organizationId: filters.organizationId.toString() }),
        ...(filters?.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters?.dateTo && { dateTo: filters.dateTo }),
      });

      const response = await api.get<PaginatedResponse<CertificateListItem>>(
        `/certificates?${queryParams.toString()}`
      );

      if (import.meta.env.DEV) {
        console.log('📊 Certificates API Response:', response.data);
      }

      if (!response.data) {
        throw new Error('Invalid response format: missing data');
      }

      const responseData: any = response.data;

      // Spring Data Pageable shape (content + pageable)
      if (responseData.content && responseData.pageable) {
        const normalized = responseData.content.map((cert: any) => ({
          ...cert,
          id: cert.id?.toString() || cert.id,
          status: calculateStatus(cert.expiresAt),
        }));

        return {
          data: normalized,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // If API returned a plain array or no pagination property, build pagination locally
      if (!response.data.pagination) {
        const items = Array.isArray(response.data) ? response.data : [];
        const total = items.length;
        const totalPages = Math.max(1, Math.ceil(total / params.size));
        const start = params.page * params.size;
        const pagedItems = items.slice(start, start + params.size).map((cert: any) => ({
          ...cert,
          id: cert.id?.toString() || cert.id,
          status: calculateStatus(cert.expiresAt),
        }));

        return {
          data: pagedItems,
          pagination: {
            page: params.page,
            size: params.size,
            total,
            totalPages,
            hasNext: params.page < totalPages - 1,
            hasPrev: params.page > 0,
          },
        };
      }

      // Already in expected PaginatedResponse shape — ensure status calculated
      const mapped = response.data.data?.map((cert: any) => ({
        ...cert,
        id: cert.id?.toString() || cert.id,
        status: calculateStatus(cert.expiresAt),
      }));

      return {
        ...response.data,
        data: mapped || response.data.data || [],
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Certificate by ID
   * Fetches detailed information about a specific certificate
   * 
   * @param id - Certificate ID
   * @returns Promise with certificate details
   */
  getCertificateById: async (id: string): Promise<CertificateListItem> => {
    try {
      // Mock implementation
      const certificate = MOCK_CERTIFICATES.find((cert) => cert.id === id);

      if (!certificate) {
        throw new Error('Certificate not found');
      }

      return {
        ...certificate,
        status: calculateStatus(certificate.expiresAt),
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Certificate by Reference (UUID)
   * Fetches detailed information about a specific certificate using reference
   * 
   * @param reference - Certificate reference (UUID)
   * @returns Promise with certificate details
   */
  getCertificateByReference: async (reference: string): Promise<CertificateListItem> => {
    try {
      // Mock implementation
      const certificate = MOCK_CERTIFICATES.find((cert) => cert.ref === reference);

      if (!certificate) {
        throw new Error('Certificate not found');
      }

      return {
        ...certificate,
        status: calculateStatus(certificate.expiresAt),
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

 /**
 * Create Certificate
 * Creates a new certificate by uploading a PFX/P12 file
 * 
 * @param file - Certificate file (PFX/P12)
 * @param metadata - Certificate metadata including password and alias
 * @returns Promise with created certificate
 */
createCertificate: async (
  certificateData: CreateCertificatePayload
): Promise<CertificateListItem> => {
  try {
    const token = localStorage.getItem('alz-token');
    console.log('Token:', token);
    const formData = new FormData();
    formData.append('file', certificateData.file);
    const metadataBlob = new Blob([JSON.stringify(certificateData.metadata)], {
      type: 'application/json',
    });
    formData.append('metadata', metadataBlob);
    console.log(formData.get('metadata'));
    const response = await api.post<CertificateListItem>(
      '/certificates/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      }
    );

    // Normalize response
    return {
      ...response.data,
      id: response.data.id?.toString() || '',
      ref: response.data.ref || '',
      alias: response.data.alias || '',
      //serialNumber: response.data.serialNumber || '',
      status: response.data.status || 'ACTIVE',
      //issuedAt: response.data.issuedAt || '',
      expiresAt: response.data.expiresAt || '',
      createdAt: response.data.createdAt || '',
      updatedAt: response.data.updatedAt || '',
      organizationId: response.data.organizationId || 1,
    };
  } catch (error) {
    const apiError = handleApiError(error as any);
    throw new Error(apiError.message);
  }
},

  /**
   * Update Certificate
   * Updates an existing certificate's metadata (alias, password)
   * 
   * @param reference - Certificate reference (UUID)
   * @param data - Partial certificate data to update
   * @returns Promise with updated certificate
   */
  updateCertificate: async (
    reference: string,
    data: UpdateCertificatePayload
  ): Promise<CertificateListItem> => {
    try {
      const formData = new FormData();
      const metadataBlob = new Blob([JSON.stringify(data)], {
        type: 'application/json',
      });
      formData.append('metadata', metadataBlob);

      const response = await api.put<CertificateListItem>(
        `/certificates/${reference}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Normalize response
      return {
        ...response.data,
        id: response.data.id?.toString() || '',
        ref: response.data.ref || reference,
        alias: response.data.alias || '',
        status: response.data.status || 'ACTIVE',
        expiresAt: response.data.expiresAt || '',
        createdAt: response.data.createdAt || '',
        updatedAt: response.data.updatedAt || new Date().toISOString(),
        organizationId: response.data.organizationId || 1,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Delete Certificate
   * Deletes a certificate from the system by reference (UUID)
   * 
   * @param reference - Certificate reference (UUID)
   * @returns Promise that resolves when certificate is deleted
   */
  deleteCertificate: async (reference: string): Promise<void> => {
    try {
      await api.delete(`/certificates/${reference}`);
      
      if (import.meta.env.DEV) {
        console.log('🗑️ Certificate deleted:', reference);
      }
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Bulk Delete Certificates
   * Deletes multiple certificates at once
   * 
   * @param ids - Array of certificate IDs to delete
   * @returns Promise that resolves when certificates are deleted
   */
  bulkDeleteCertificates: async (ids: string[]): Promise<void> => {
    try {
      // Mock implementation
      ids.forEach((id) => {
        const index = MOCK_CERTIFICATES.findIndex((cert) => cert.id === id);
        if (index !== -1) {
          MOCK_CERTIFICATES.splice(index, 1);
        }
      });
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Export Certificates
   * Exports certificates list to CSV/Excel
   * 
   * @param filters - Optional filters for export
   * @returns Promise with file blob
   */
  exportCertificates: async (filters?: CertificateFilters): Promise<Blob> => {
    try {
      // Mock implementation - create CSV blob
      let certificatesToExport = [...MOCK_CERTIFICATES];

      if (filters?.status) {
        certificatesToExport = certificatesToExport.filter(
          (cert) => calculateStatus(cert.expiresAt) === filters.status
        );
      }

      if (filters?.organizationId) {
        certificatesToExport = certificatesToExport.filter(
          (cert) => cert.organizationId === filters.organizationId
        );
      }

      // Create CSV content
      const headers = ['ID', 'Reference', 'Alias', 'Organization ID', 'Expires At', 'Created At', 'Status'];
      const rows = certificatesToExport.map((cert) => [
        cert.id,
        cert.ref,
        cert.alias,
        cert.organizationId,
        cert.expiresAt,
        cert.createdAt,
        calculateStatus(cert.expiresAt),
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      return new Blob([csvContent], { type: 'text/csv' });
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },
};

export default certificateService;
